/**
 * Copyright Highway9 Networks Inc.
 *
 * Notification sync + session auto-download for operation websocket patches.
 * Runs after Entity State apply so operation lookups see the patched entities.
 */
import { store } from "..";
import type { WebSocketMessage } from "~/types/WebsocketMessage";
import { settingsOperationsEntities } from "../context/settingsSlice";
import { notificationActions, selectNotificationByOperationId } from "../context/notificationsSlice";
import {
  getDownloadUrlFromOutput,
  createNotificationFromOperation,
} from "~/components/Notifications/utils";
import { getSessionInitiatedOperations, operationService } from "~/services";

const downloadingOperations = new Set<string>();

const handleAutomaticDownload = async (operationId: string, url: string) => {
  try {
    store.dispatch(
      notificationActions.updateNotificationByOperationId({
        operationId,
        updates: {
          subtitle: "Downloading file...",
          status: "in-progress",
        },
      }),
    );

    const success = await operationService.DownloadFile(url);

    if (success) {
      store.dispatch(
        notificationActions.updateNotificationByOperationId({
          operationId,
          updates: {
            status: "completed",
            subtitle: "File downloaded successfully",
          },
        }),
      );
    } else {
      store.dispatch(
        notificationActions.updateNotificationByOperationId({
          operationId,
          updates: {
            status: "failed",
            subtitle: "Download failed",
          },
        }),
      );
    }
  } catch (error) {
    console.error("Automatic download error:", error);
    store.dispatch(
      notificationActions.updateNotificationByOperationId({
        operationId,
        updates: {
          status: "failed",
          subtitle: "Download error: " + (error as Error).message,
        },
      }),
    );
  } finally {
    downloadingOperations.delete(operationId);
  }
};

/**
 * Sync notifications (and optional session downloads) for each operation patch.
 */
// Handles syncing notification state (and session downloads) as a side effect of websocket operation patch updates.
export const syncOperationSocketSideEffects = async (patches: WebSocketMessage[]) => {
  // Get the current operations from settings.
  // Entity map, not selectAll: this runs on every operations flush on every route,
  // and the per-patch lookup below was a linear scan of a collection that grows
  // for the life of the tab (MCS-4316)
  const operationsById = settingsOperationsEntities(store.getState());
  // Read sessionStorage + parse ONCE per flush, not per patch
  const sessionOperations = new Set(getSessionInitiatedOperations());

  for (const patch of patches) {
    const id = patch.objectId;

    // Skip if patch doesn't have modified values or an operation ID.
    if (!patch.modifiedValues || !id) continue;

    // See if a notification already exists for this operation.
    const notification = selectNotificationByOperationId(store.getState(), id);

    // Extract relevant fields from patch changes.
    const { status, statusMessage, output, input, completionTime } = patch.modifiedValues;

    // Find the current operation in state (could be undefined).
    const currentOperation = operationsById[id];

    // Determine if this is a radio monitoring operation (special cased).
    const isRadioMonitoring = currentOperation?.operationType === "START_RADIO_MONITORING";

    // Extract a file download URL from the operation output, if present.
    const url = getDownloadUrlFromOutput(output);

    // If there's a URL to download and it's not radio monitoring:
    if (url && !isRadioMonitoring) {
      // Prevent duplicate downloads by checking if we're already downloading for this ID.
      if (downloadingOperations.has(id)) continue;

      // If this operation was initiated in this session, trigger the download.
      // Fire and forget - awaiting blocked notification sync for the rest of
      // the batch behind a file download
      if (sessionOperations.has(id)) {
        downloadingOperations.add(id); // Mark as downloading.
        void handleAutomaticDownload(id, url); // Start the file download.
      }
    }

    // If there's already a notification for this operation, sync with latest patch values.
    if (notification) {
      store.dispatch(
        notificationActions.syncWithOperation({
          operationId: id,
          operationName: currentOperation?.operationType,
          source: "websocket",
          status,
          statusMessage,
          output,
          input,
          completionTime,
        }),
      );
    } else {
      // Otherwise, if the operation exists, create a new notification from it.
      if (!currentOperation) continue;
      const newNotification = createNotificationFromOperation(currentOperation, "websocket");
      store.dispatch(notificationActions.addNotification(newNotification));
    }
  }
};
